import { useEffect, useRef, useState } from 'react';

/**
 * How long a request may run before a placeholder is shown at all.
 *
 * Under this, the reader perceives the load as instant and a placeholder would be a
 * flash of grey they cannot read — worse than showing nothing (spec 079 FR-017).
 */
const APPEARANCE_DELAY_MS = 160;

/**
 * How long a placeholder stays once it has appeared.
 *
 * Without this, data arriving just after the delay paints a placeholder for a frame or
 * two and rips it away — the exact flicker the delay was meant to prevent, moved rather
 * than removed (FR-018).
 */
const MINIMUM_VISIBLE_MS = 320;

/*
 * Neither value comes from the motion scale, and that is deliberate. Those tokens
 * describe how long a thing takes to *move*; these describe how long a reader must wait
 * before they notice, and how long they need to register what they noticed. Borrowing
 * `--motion-duration-slow` here would look like rigour while meaning nothing, and would
 * tie two unrelated things together so that tuning one silently changed the other.
 */

/**
 * Whether a placeholder should be on screen right now (spec 079 FR-017–FR-020).
 *
 * Two guards, because they close different holes and neither closes both: the delay
 * removes the flash when data beats it, the minimum removes the flash when data arrives
 * just after the placeholder appeared. Shipping only the delay leaves the second case,
 * which is the common one on a warm cache.
 *
 * Centralised rather than repeated at eleven call sites, because of the transition in
 * the middle of the table below: a request that finishes *during* the delay must cancel
 * its timer. A hand-rolled copy that forgets leaves a `setTimeout` to fire after the
 * content has already rendered, painting a placeholder over loaded data — a bug that
 * only appears on fast connections and therefore not on the machine it was written on.
 *
 * | From    | Event                                  | To                            |
 * |---------|----------------------------------------|-------------------------------|
 * | idle    | becomes pending                        | waiting (armed, nothing shown)|
 * | waiting | delay elapses                          | visible (armed for minimum)   |
 * | waiting | stops pending                          | idle — **timer cancelled**    |
 * | visible | stops pending, minimum not yet elapsed | visible, held                 |
 * | visible | minimum elapsed, not pending           | idle                          |
 */
export function usePendingDisplay(isPending: boolean): boolean {
  const [isVisible, setIsVisible] = useState(false);
  /** When the placeholder appeared, so the minimum can be measured from it. */
  const shownAt = useRef<number | null>(null);

  useEffect(() => {
    if (isPending) {
      if (isVisible) return;
      const timer = setTimeout(() => {
        shownAt.current = Date.now();
        setIsVisible(true);
      }, APPEARANCE_DELAY_MS);
      // The cancellation the table's third row describes: this runs when `isPending`
      // goes false mid-delay, so no placeholder is ever scheduled into the future.
      return () => {
        clearTimeout(timer);
      };
    }

    if (!isVisible) return;

    const elapsed = shownAt.current === null ? 0 : Date.now() - shownAt.current;
    const remaining = MINIMUM_VISIBLE_MS - elapsed;
    if (remaining <= 0) {
      shownAt.current = null;
      setIsVisible(false);
      return;
    }

    const timer = setTimeout(() => {
      shownAt.current = null;
      setIsVisible(false);
    }, remaining);
    return () => {
      clearTimeout(timer);
    };
  }, [isPending, isVisible]);

  return isVisible;
}
